/**
 * Escalation Card — F7 tracked async human-escalation (12-factor #7)
 *
 * Bespoke War Room card (mounted on WarRoom.tsx, /deliberation/:reportId) that
 * lists tracked "an agent needs a human to decide X" escalations — the
 * DECISION_REQUIRED ActionItems the consensus reducer opens for the War Room's
 * unresolved agent-conflict disputes — and lets the attorney resolve each one.
 *
 * Design of record: docs/plans/2026-09-26-F7-tracked-escalation-design.md
 *
 * Behaviors baked in from the design:
 *  - Reads GET /action-items/{reportId}?action_type=decision_required (open +
 *    resolved). Resolve uses the EXISTING POST /action-items/{id}/resolve.
 *  - A 400 "already resolved" is treated as success (refetch), since the resolve
 *    endpoint is not idempotent.
 *  - GRACE RE-POLL: the reducer that opens the item runs AFTER the fleet
 *    finalizes `completed`, so WarRoom's single on-complete fetch can miss a
 *    just-created item. On `refreshSignal` change (WarRoom bumps it on completion
 *    + manual refresh) the card re-fetches immediately, then again over a short
 *    30s grace window.
 *  - Renders nothing when there are no escalations (keeps clean cases uncluttered).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, ShieldQuestion } from 'lucide-react';
import {
  getEscalations,
  resolveEscalation,
  type EscalationItem,
} from '@/services/deliberationApi';

export interface EscalationCardProps {
  reportId: string;
  /** Bumped by the parent (WarRoom) on run completion + manual refresh to
   *  trigger a grace re-poll for a just-created item. */
  refreshSignal?: number;
}

const RESOLVED_STATUSES = new Set(['resolved']);
const CLOSED_STATUSES = new Set(['resolved', 'cancelled']);

export function EscalationCard({ reportId, refreshSignal = 0 }: EscalationCardProps) {
  const [items, setItems] = useState<EscalationItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchEscalations = useCallback(async () => {
    try {
      const resp = await getEscalations(reportId);
      setItems(resp.items || []);
    } catch {
      // 404 (org-scoped / no escalations) is a legitimate empty state — render nothing.
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, [reportId]);

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    const run = () => {
      if (!cancelled) fetchEscalations();
    };
    run();
    // Grace re-poll (immediate + 3×10s). Cheap: at most 4 GETs over 30s per signal.
    const timers = [
      setTimeout(run, 10_000),
      setTimeout(run, 20_000),
      setTimeout(run, 30_000),
    ];
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [reportId, refreshSignal, fetchEscalations]);

  const openItems = useMemo(
    () => items.filter((i) => !CLOSED_STATUSES.has(i.status)),
    [items],
  );
  const resolvedItems = useMemo(
    () => items.filter((i) => RESOLVED_STATUSES.has(i.status)),
    [items],
  );

  // Nothing to show until we've loaded and there is at least one escalation.
  if (!loaded || items.length === 0) return null;

  return (
    <section
      data-testid="escalation-card"
      className="rounded-xl shadow-sm"
      style={{
        background: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div
        className="flex items-center gap-2 px-5 py-4"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        <ShieldQuestion className="h-5 w-5 shrink-0" style={{ color: 'var(--color-warning)' }} />
        <div>
          <h3 className="cs-h3" style={{ color: 'var(--color-text-primary)' }}>
            Decisions Required
          </h3>
          <p className="cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
            The agents reached an impasse and need a human to decide. Resolving a
            decision does not re-run the analysis.
          </p>
        </div>
        <span
          data-testid="escalation-open-count"
          className="cs-chip ml-auto px-2 py-0.5 rounded-full"
          style={{
            background: openItems.length
              ? 'var(--color-warning-bg, var(--color-bg-elevated))'
              : 'var(--color-bg-elevated)',
            color: openItems.length ? 'var(--color-warning)' : 'var(--color-text-muted)',
          }}
        >
          {openItems.length} open
        </span>
      </div>

      <div className="px-5 py-4 space-y-4">
        {openItems.length === 0 && (
          <p className="cs-meta" style={{ color: 'var(--color-text-secondary)' }}>
            No open decisions — all escalations on this case are resolved.
          </p>
        )}

        {openItems.map((item) => (
          <OpenEscalationRow
            key={item.id}
            item={item}
            onResolved={fetchEscalations}
          />
        ))}

        {resolvedItems.length > 0 && (
          <details data-testid="escalation-resolved" className="pt-1">
            <summary
              className="cs-label cursor-pointer select-none"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Resolved ({resolvedItems.length})
            </summary>
            <div className="mt-3 space-y-3">
              {resolvedItems.map((item) => (
                <ResolvedEscalationRow key={item.id} item={item} />
              ))}
            </div>
          </details>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

function IssueTagChip({ item }: { item: EscalationItem }) {
  const issueTag = (item.metadata?.issue_tag as string) || '';
  const runId = (item.metadata?.run_id as string) || '';
  if (!issueTag && !runId) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      {issueTag && (
        <span
          className="cs-chip px-1.5 py-0.5 rounded font-mono"
          style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
          title="issue_tag"
        >
          {issueTag}
        </span>
      )}
      {runId && (
        <span className="cs-meta font-mono" style={{ color: 'var(--color-text-dim)' }} title="run_id">
          run {runId}
        </span>
      )}
    </div>
  );
}

function OpenEscalationRow({
  item,
  onResolved,
}: {
  item: EscalationItem;
  onResolved: () => void;
}) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await resolveEscalation(item.id, notes.trim());
      onResolved();
    } catch (err: any) {
      // The resolve endpoint 400s on an already-resolved item — treat that as
      // success and refetch so the row moves to the resolved list.
      if (err?.status === 400) {
        onResolved();
        return;
      }
      setError(err?.message || 'Failed to resolve');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="escalation-open-row"
      className="rounded-lg p-4"
      style={{
        background: 'var(--color-bg-base)',
        border: '1px solid var(--color-border)',
        borderLeft: '3px solid var(--color-warning)',
      }}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--color-warning)' }} />
        <div className="min-w-0">
          <p className="cs-label" style={{ color: 'var(--color-text-primary)' }}>
            {item.title}
          </p>
          <p className="cs-body mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            {item.description}
          </p>
          <IssueTagChip item={item} />
        </div>
      </div>

      <form onSubmit={handleResolve} className="mt-3 space-y-2">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Record the human decision (how this was resolved)…"
          rows={2}
          disabled={submitting}
          className="w-full px-3 py-2 rounded-lg cs-body"
          style={{
            background: 'var(--color-bg-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
            resize: 'none',
          }}
        />
        {error && (
          <p className="cs-meta" style={{ color: 'var(--color-needs-human-text)' }}>
            {error}
          </p>
        )}
        <div className="flex items-center justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="cs-label flex items-center gap-2 px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"
            style={{ color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
            onMouseEnter={(e) => {
              if (!submitting) e.currentTarget.style.background = 'var(--color-brand-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-brand)';
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Resolving…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Mark Resolved
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResolvedEscalationRow({ item }: { item: EscalationItem }) {
  const resolvedAt = item.resolved_at
    ? new Date(item.resolved_at).toLocaleString()
    : null;
  return (
    <div
      data-testid="escalation-resolved-row"
      className="rounded-lg p-4"
      style={{
        background: 'var(--color-bg-base)',
        border: '1px solid var(--color-border)',
        borderLeft: '3px solid var(--color-verified)',
      }}
    >
      <div className="flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--color-verified)' }} />
        <div className="min-w-0">
          <p className="cs-label" style={{ color: 'var(--color-text-primary)' }}>
            {item.title}
          </p>
          <p className="cs-body mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            {item.description}
          </p>
          <IssueTagChip item={item} />
          {item.resolution_notes && (
            <p
              className="cs-body mt-2 pl-3"
              style={{ color: 'var(--color-text-primary)', borderLeft: '2px solid var(--color-verified)' }}
            >
              <span className="cs-meta" style={{ color: 'var(--color-text-dim)' }}>Resolution: </span>
              {item.resolution_notes}
            </p>
          )}
          {resolvedAt && (
            <p className="cs-meta mt-1" style={{ color: 'var(--color-text-dim)' }}>
              Resolved {resolvedAt}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default EscalationCard;
